import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import type { OrderListItem, OrderStatus } from '@expeditor/shared';
import { managerOrdersApi } from '../../shared/api/orders';
import {
  ORDER_STATUS_LABEL,
  orderStatusColor,
  orderStatusLabel,
} from '../../shared/ui/order-labels';

export function AllOrdersPage() {
  const [items, setItems] = useState<OrderListItem[]>([]);
  const [status, setStatus] = useState<OrderStatus | ''>('');
  const [clientId, setClientId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      setError(null);
      const data = await managerOrdersApi.list({
        ...(status ? { status } : {}),
        ...(clientId ? { clientId } : {}),
      });
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, clientId]);

  return (
    <Stack>
      <Title order={2}>Все поручения</Title>

      <Group>
        <Select
          placeholder="Все статусы"
          data={Object.entries(ORDER_STATUS_LABEL).map(([v, l]) => ({ value: v, label: l }))}
          value={status || null}
          onChange={(v) => setStatus((v as OrderStatus) || '')}
          clearable
          w={180}
        />
        <TextInput
          placeholder="ID клиента (точное совпадение)"
          value={clientId}
          onChange={(e) => setClientId(e.currentTarget.value)}
          w={240}
        />
      </Group>

      {error && <Text c="red">{error}</Text>}

      <Table striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Номер</Table.Th>
            <Table.Th>Клиент</Table.Th>
            <Table.Th>Статус</Table.Th>
            <Table.Th>Груз</Table.Th>
            <Table.Th>Вес, кг</Table.Th>
            <Table.Th>Создано</Table.Th>
            <Table.Th style={{ width: 60 }} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((o) => (
            <Table.Tr key={o.id}>
              <Table.Td>
                <Text fw={500} c="blue" component={Link} to={`/manager/orders/${o.id}`}>
                  {o.number}
                </Text>
              </Table.Td>
              <Table.Td>{o.clientDisplayName ?? o.clientId}</Table.Td>
              <Table.Td>
                <Badge color={orderStatusColor(o.status)}>{orderStatusLabel(o.status)}</Badge>
              </Table.Td>
              <Table.Td>{o.cargoName}</Table.Td>
              <Table.Td>{o.cargoWeightKg}</Table.Td>
              <Table.Td>{new Date(o.createdAt).toLocaleString('ru-RU')}</Table.Td>
              <Table.Td>
                <Tooltip label="Просмотр">
                  <ActionIcon
                    variant="subtle"
                    component={Link}
                    to={`/manager/orders/${o.id}`}
                  >
                    <IconEye size={16} />
                  </ActionIcon>
                </Tooltip>
              </Table.Td>
            </Table.Tr>
          ))}
          {items.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Text c="dimmed" ta="center" py="md">
                  Поручений не найдено
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
